import { createClient } from '@supabase/supabase-js';
import { BUCKET_NAME, isSupabaseConfigured, supabaseUrl } from './supabase';

// Define the folder in the Supabase storage where avatar images are stored
export const PROFILES_FOLDER = 'profiles';

// Define the public URL format for avatars
export const PUBLIC_AVATAR_URL_BASE = `${supabaseUrl}/storage/v1/object/public/${BUCKET_NAME}/${PROFILES_FOLDER}`;

/**
 * Generate a public URL for an avatar image (no signing needed)
 * @param filename The filename of the avatar in the profiles folder
 * @returns The public URL string for the avatar
 */
export function createPublicAvatarUrl(filename: string): string {
  // Clean the filename to ensure it doesn't have the folder path
  let cleanFilename = filename;
  
  // Handle paths that might include the folder name
  if (cleanFilename.includes(`${PROFILES_FOLDER}/`)) {
    cleanFilename = cleanFilename.split(`${PROFILES_FOLDER}/`)[1];
  }
  
  // Use the hard-coded URL format the user specified
  return `https://rwwwynlbqvbshnddbhlj.supabase.co/storage/v1/object/public/${BUCKET_NAME}/${PROFILES_FOLDER}/${cleanFilename}`;
}

/**
 * Simple hash function for strings
 */
function hashString(str: string): number {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32bit integer
  }
  return hash;
}

/**
 * List all available avatars from the storage bucket
 * @param supabase The supabase client instance
 * @returns Promise resolving to an array of avatar objects with URLs
 */
export async function listAvailableAvatars(supabase: any): Promise<any[]> {
  try {
    const { data, error } = await supabase
      .storage
      .from(BUCKET_NAME)
      .list(PROFILES_FOLDER);
      
    if (error) {
      console.error('Error listing avatars:', error);
      return [];
    }
    
    // Filter to include only image files (svg, png, jpg)
    return data
      .filter((file: any) => {
        const extension = file.name.split('.').pop()?.toLowerCase();
        return !file.id.startsWith('.') && ['svg', 'png', 'jpg', 'jpeg'].includes(extension);
      })
      .map((file: any) => ({
        id: file.id,
        name: file.name,
        url: `${PROFILES_FOLDER}/${file.name}`
      }));
  } catch (err) {
    console.error('Exception listing avatars:', err);
    return [];
  }
}

// Default fallback avatars when Supabase storage is not available
export const fallbackAvatars = [
  { id: 1, name: "avatar1.svg", url: "https://ui-avatars.com/api/?name=1&color=7F9CF5&background=EBF4FF" },
  { id: 2, name: "avatar2.svg", url: "https://ui-avatars.com/api/?name=2&color=FFA500&background=FFEFD5" },
  { id: 3, name: "avatar3.svg", url: "https://ui-avatars.com/api/?name=3&color=008000&background=F0FFF0" },
  { id: 4, name: "avatar4.svg", url: "https://ui-avatars.com/api/?name=4&color=FF0000&background=FFF0F0" },
  { id: 5, name: "avatar5.svg", url: "https://ui-avatars.com/api/?name=5&color=800080&background=F5F0FF" },
  { id: 6, name: "avatar6.svg", url: "https://ui-avatars.com/api/?name=6&color=0000FF&background=F0F0FF" },
  { id: 7, name: "avatar7.svg", url: "https://ui-avatars.com/api/?name=7&color=FF00FF&background=FFF0FF" },
  { id: 8, name: "avatar8.svg", url: "https://ui-avatars.com/api/?name=8&color=006400&background=F0FFF0" },
  { id: 9, name: "avatar9.svg", url: "https://ui-avatars.com/api/?name=9&color=FF4500&background=FFEEE0" },
  { id: 10, name: "avatar10.svg", url: "https://ui-avatars.com/api/?name=0&color=1E90FF&background=F0F8FF" }
]; 